/**
 * Shared purpose mapping utilities for Layer 4 platform adapters.
 *
 * Maps platform-native action names to PDLSS purpose categories.
 * Used by OpenClaw CLI, Cursor, browser, and future adapters.
 */

// -----------------------------------------------------------------------
// Tool → Purpose mapping
// -----------------------------------------------------------------------

/** Standard tool name → PDLSS purpose mapping used by all adapters */
const TOOL_PURPOSE_MAP: Record<string, string> = {
  // Shell
  shell_exec: 'shell.exec',
  run_command: 'shell.exec',
  execute: 'shell.exec',
  terminal_exec: 'shell.exec',
  run_terminal_command: 'shell.exec',

  // File read
  file_read: 'file.read',
  read_file: 'file.read',

  // File write
  file_write: 'file.write',
  write_file: 'file.write',
  create_file: 'file.write',
  edit_file: 'file.write',

  // File delete
  file_delete: 'file.delete',
  delete_file: 'file.delete',

  // Network
  http_request: 'network.request',
  fetch: 'network.request',
  web_request: 'network.request',

  // Email
  send_email: 'email.send',
  read_email: 'email.read',

  // Calendar
  create_event: 'calendar.create',

  // Database
  query_database: 'database.query',
  write_database: 'database.write',

  // Payment
  payment_execute: 'payment.execute',
};

/**
 * Map a tool/action name to a PDLSS purpose category.
 * Returns `tool.<name>` for unmapped tools (denied by default).
 */
export function mapToolToPurpose(toolName: string): string {
  return TOOL_PURPOSE_MAP[toolName] || `tool.${toolName}`;
}

/**
 * Register additional tool → purpose mappings (e.g. from a platform adapter).
 * Does not overwrite existing mappings.
 */
export function registerToolMappings(mappings: Record<string, string>): void {
  for (const [tool, purpose] of Object.entries(mappings)) {
    if (!(tool in TOOL_PURPOSE_MAP)) {
      TOOL_PURPOSE_MAP[tool] = purpose;
    }
  }
}

// -----------------------------------------------------------------------
// Target extraction
// -----------------------------------------------------------------------

/**
 * Extract the meaningful target string from tool arguments.
 * Uses the purpose category to determine which argument field is relevant.
 */
export function extractTarget(toolName: string, args: Record<string, unknown>): string {
  const purpose = mapToolToPurpose(toolName);

  if (purpose.startsWith('shell.')) {
    return String(args.command || args.cmd || args.script || '');
  }

  if (purpose.startsWith('file.')) {
    return String(args.path || args.file || args.filename || args.file_path || '');
  }

  if (purpose.startsWith('network.')) {
    return String(args.url || args.endpoint || args.uri || '');
  }

  if (purpose.startsWith('email.')) {
    return String(args.to || args.recipient || args.address || '');
  }

  if (purpose.startsWith('database.')) {
    return String(args.query || args.table || '');
  }

  if (purpose.startsWith('payment.')) {
    return String(args.description || args.merchant || args.amount || '');
  }

  // Fallback: try common field names
  if (args.command) return String(args.command);
  if (args.path) return String(args.path);
  if (args.url) return String(args.url);

  // Default: use first non-empty string argument or tool name
  for (const val of Object.values(args)) {
    if (typeof val === 'string' && val.length > 0) return val;
  }
  return toolName;
}

// -----------------------------------------------------------------------
// Network domain extraction
// -----------------------------------------------------------------------

/**
 * Extract network domains from a URL target.
 * Returns undefined if the target is not a URL.
 */
export function extractNetworkDomains(target: string): string[] | undefined {
  try {
    if (target.startsWith('http://') || target.startsWith('https://')) {
      const url = new URL(target);
      return [url.hostname];
    }
  } catch {
    // Not a URL
  }
  return undefined;
}
